import { nanoid } from 'nanoid'
import { v4 as uuid } from 'uuid'
import {
  BULK_ORDER_GRID_AXES,
  BULK_ORDER_GRID_AXIS_VALUES,
  BULK_ORDER_GRID_SIGNS,
  MAX_CART_LINE_QUANTITY,
  MAX_CART_LINES,
} from './config'

export const DEFAULT_GRID_CONTEXT = {
  diameter: null,
  side: 'B',
  cylinderSign: BULK_ORDER_GRID_SIGNS.PLUS,
  additionSign: BULK_ORDER_GRID_SIGNS.PLUS,
}

export const CART_LOAD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed',
}

export const CART_SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  FAILED: 'failed',
}

export const SUBMIT_STATUS = {
  IDLE: 'idle',
  SUBMITTING: 'submitting',
  FAILED: 'failed',
}

export const DEFAULT_CART_STATE = {
  lines: [],
  reference: '',
  remark: '',
  cartId: null,
  serverRevision: null,
  submissionId: null,
  dirty: false,
  cartOwnerId: null,
  loadStatus: CART_LOAD_STATUS.IDLE,
  saveStatus: CART_SAVE_STATUS.IDLE,
  hasCartSaveError: false,
}

/**
 * Server-owned draft token; one BulkOrder per value (unique index).
 * Char(20) nanoid — same length as pair-order xmlOrderId / wholesale JOBNUMB.
 */
export const createSubmissionId = () => {
  const id = nanoid(20)
  if (id.indexOf('~') !== -1) {
    return createSubmissionId()
  }
  return id
}

/** Stable error code in PUT conflict responses (survives api.js dropping HTTP status). */
export const BULK_ORDER_CART_CONFLICT_CODE = 'BULK_ORDER_CART_CONFLICT'

export const isBulkOrderCartConflictError = error => {
  const errors = error?.errors
  if (!Array.isArray(errors)) return false
  return errors.some(item => item?.code === BULK_ORDER_CART_CONFLICT_CODE)
}

const VALID_SIDES = ['R', 'L', 'B']

export const MIN_CART_LINE_QUANTITY = 1
export const CART_META_MAX_LENGTH = 30

export const isValidCartLineQuantity = quantity =>
  Number.isInteger(quantity) &&
  quantity >= MIN_CART_LINE_QUANTITY &&
  quantity <= MAX_CART_LINE_QUANTITY

/** Snapshot display axis; missing/invalid → cylinder (legacy carts). */
export const normalizeGridAxis = gridAxis =>
  BULK_ORDER_GRID_AXIS_VALUES.includes(gridAxis) ? gridAxis : BULK_ORDER_GRID_AXES.CYLINDER

export const normalizeCartMeta = value => {
  if (value == null) return ''
  return String(value).slice(0, CART_META_MAX_LENGTH)
}

/**
 * @param {string|number} value
 * @param {{ currentQuantity?: number, emptyMeans?: 'remove' | 'min' }} [options]
 * @returns {number|null} parsed qty, 0 to signal remove (grid), or null if invalid / no-op
 */
export const parseCartQuantity = (
  value,
  { currentQuantity = 0, emptyMeans = 'min' } = {},
) => {
  const hasLine = currentQuantity > 0
  const emptyOrNonPositiveResult =
    emptyMeans === 'remove' ? 0 : MIN_CART_LINE_QUANTITY

  if (value === '') {
    return hasLine ? emptyOrNonPositiveResult : null
  }

  let parsed
  if (typeof value === 'number') {
    if (!Number.isInteger(value)) {
      return hasLine ? MIN_CART_LINE_QUANTITY : null
    }
    parsed = value
  } else if (typeof value === 'string' && /^\d+$/.test(value)) {
    parsed = Number(value)
  } else {
    return hasLine ? MIN_CART_LINE_QUANTITY : null
  }

  if (parsed <= 0) {
    return hasLine ? emptyOrNonPositiveResult : null
  }

  if (parsed > MAX_CART_LINE_QUANTITY) {
    return null
  }

  return parsed
}

export const parseEditableCartQuantity = (value, currentQuantity = 0) =>
  parseCartQuantity(value, { currentQuantity, emptyMeans: 'min' })

export const parseGridCartQuantity = (value, currentQuantity = 0) =>
  parseCartQuantity(value, { currentQuantity, emptyMeans: 'remove' })

export const getCartLineKey = ({ lensId, diameter, side, sphere, cylinder, addition = 0 }) =>
  `${lensId}|${diameter}|${side}|${sphere}|${cylinder}|${addition}`

export const createCartLine = ({
  lensId,
  lensCode,
  lensName,
  diameter,
  side,
  sphere,
  cylinder,
  addition = 0,
  gridAxis = BULK_ORDER_GRID_AXES.CYLINDER,
  quantity,
  id = uuid(),
}) => ({
  id,
  lensId: String(lensId),
  lensCode,
  lensName,
  diameter,
  side,
  sphere,
  cylinder,
  addition,
  gridAxis: normalizeGridAxis(gridAxis),
  quantity,
})

const isNonNaNNumber = value => typeof value === 'number' && !Number.isNaN(value)

const hasRequiredCartLineIds = line => Boolean(line?.id && line.lensId && line.lensCode)

const isValidCartLine = line => {
  if (!line || typeof line !== 'object') return false
  if (!hasRequiredCartLineIds(line)) return false
  if (typeof line.lensName !== 'string') return false
  if (!isNonNaNNumber(line.diameter)) return false
  if (!VALID_SIDES.includes(line.side)) return false
  if (!isNonNaNNumber(line.sphere)) return false
  if (!isNonNaNNumber(line.cylinder) || line.cylinder < 0) return false
  if (!isNonNaNNumber(line.addition ?? 0)) return false
  return isValidCartLineQuantity(line.quantity)
}

const toNormalizedCartLine = line => ({
  id: line.id,
  lensId: String(line.lensId),
  lensCode: line.lensCode,
  lensName: line.lensName,
  diameter: line.diameter,
  side: line.side,
  sphere: line.sphere,
  cylinder: line.cylinder,
  addition: line.addition ?? 0,
  gridAxis: normalizeGridAxis(line.gridAxis),
  quantity: line.quantity,
})

export const normalizeCartLines = lines => {
  if (!Array.isArray(lines)) return []

  return lines.filter(isValidCartLine).map(toNormalizedCartLine)
}

/** Strict PUT validation: reject invalid quantities and duplicate composite keys. */
const INVALID_CART_LINES_RESULT = {
  ok: false,
  errors: [{ message: 'Invalid bulk order cart lines' }],
}

export const validateCartLinesPayload = lines => {
  if (!Array.isArray(lines)) {
    return INVALID_CART_LINES_RESULT
  }

  if (lines.length > MAX_CART_LINES) {
    return INVALID_CART_LINES_RESULT
  }

  const seenKeys = new Set()
  const normalized = []

  for (const line of lines) {
    if (!isValidCartLine(line)) {
      return INVALID_CART_LINES_RESULT
    }

    const key = getCartLineKey(line)
    if (seenKeys.has(key)) {
      return INVALID_CART_LINES_RESULT
    }
    seenKeys.add(key)
    normalized.push(toNormalizedCartLine(line))
  }

  return { ok: true, lines: normalized }
}

export const cartToServerPayload = cart => {
  const validated = validateCartLinesPayload(cart?.lines || [])
  if (!validated.ok) {
    throw new Error('Invalid bulk order cart lines')
  }

  return {
    reference: normalizeCartMeta(cart?.reference),
    remark: normalizeCartMeta(cart?.remark),
    lines: validated.lines,
    expectedRevision: cart?.serverRevision ?? 0,
  }
}

export const normalizeCartResponse = data => {
  if (!data || typeof data !== 'object') {
    return {
      lines: [],
      reference: '',
      remark: '',
      cartId: null,
      serverRevision: null,
      submissionId: null,
      droppedLineCount: 0,
    }
  }

  const rawLines = data.lines
  const lines = normalizeCartLines(rawLines)
  const droppedLineCount = Array.isArray(rawLines) ? rawLines.length - lines.length : 0
  const submissionId =
    data.submissionId == null || data.submissionId === '' ? null : String(data.submissionId)

  return {
    lines,
    reference: normalizeCartMeta(data.reference),
    remark: normalizeCartMeta(data.remark),
    cartId: data.cartId || data._id || null,
    serverRevision: data.serverRevision ?? data.revision ?? null,
    submissionId,
    droppedLineCount,
  }
}

/**
 * Remap cart lines onto the current catalog by lensCode only.
 * Unmapped lines are dropped. Duplicate keys after remap merge quantities.
 *
 * @returns {{
 *   lines: object[],
 *   remappedCount: number,
 *   droppedCount: number,
 *   changed: boolean,
 *   needsPersist: boolean,
 * }}
 */
export const remapCartLinesForCatalog = (lines, catalogItems) => {
  const emptyResult = {
    lines: Array.isArray(lines) ? lines : [],
    remappedCount: 0,
    droppedCount: 0,
    changed: false,
    needsPersist: false,
  }

  if (!Array.isArray(lines) || lines.length === 0) {
    return { ...emptyResult, lines: [] }
  }

  if (!Array.isArray(catalogItems) || catalogItems.length === 0) {
    return emptyResult
  }

  const itemsByCode = new Map()
  catalogItems.forEach(item => {
    if (item == null || item._id == null) return
    if (item.code == null || item.code === '') return
    itemsByCode.set(String(item.code), item)
  })

  let remappedCount = 0
  let droppedCount = 0
  let nameRefreshed = false
  const remappedLines = []

  lines.forEach(line => {
    const byCode =
      line.lensCode != null && line.lensCode !== ''
        ? itemsByCode.get(String(line.lensCode))
        : null

    if (!byCode) {
      droppedCount += 1
      return
    }

    const nextId = String(byCode._id)
    const prevId = String(line.lensId)
    if (nextId !== prevId) {
      remappedCount += 1
    }

    const nextName =
      typeof byCode.name === 'string' ? byCode.name : line.lensName
    if (nextName !== line.lensName) {
      nameRefreshed = true
    }

    remappedLines.push({
      ...line,
      lensId: nextId,
      lensName: nextName,
    })
  })

  const mergedByKey = new Map()
  let merged = false
  remappedLines.forEach(line => {
    const key = getCartLineKey(line)
    const existing = mergedByKey.get(key)
    if (!existing) {
      mergedByKey.set(key, line)
      return
    }

    merged = true
    mergedByKey.set(key, {
      ...existing,
      quantity: Math.min(
        existing.quantity + line.quantity,
        MAX_CART_LINE_QUANTITY,
      ),
    })
  })

  const nextLines = [...mergedByKey.values()]
  const needsPersist = remappedCount > 0 || droppedCount > 0
  const changed =
    needsPersist || nameRefreshed || merged || nextLines.length !== lines.length

  return {
    lines: nextLines,
    remappedCount,
    droppedCount,
    changed,
    needsPersist,
  }
}

export const findCartLineIndexByKey = (lines, lineKey) =>
  lines.findIndex(line => getCartLineKey(line) === lineKey)

export const sortCartLinesByPower = (a, b) => {
  if (a.diameter !== b.diameter) return a.diameter - b.diameter
  if (a.sphere !== b.sphere) return a.sphere - b.sphere
  if (a.cylinder !== b.cylinder) return a.cylinder - b.cylinder
  const additionA = a.addition ?? 0
  const additionB = b.addition ?? 0
  if (additionA !== additionB) return additionA - additionB
  return a.side.localeCompare(b.side)
}

export const groupCartLinesByDiameter = lines => {
  if (!Array.isArray(lines) || lines.length === 0) return []

  const sorted = [...lines].sort(sortCartLinesByPower)
  const groups = []

  sorted.forEach(line => {
    const lastGroup = groups[groups.length - 1]

    if (lastGroup && lastGroup.diameter === line.diameter) {
      lastGroup.lines.push(line)
      lastGroup.lineCount += 1
      lastGroup.totalQuantity += line.quantity
      return
    }

    groups.push({
      diameter: line.diameter,
      lines: [line],
      lineCount: 1,
      totalQuantity: line.quantity,
    })
  })

  return groups
}

export const groupCartLinesByLensId = lines => {
  if (!Array.isArray(lines) || lines.length === 0) return []

  const linesByLensId = new Map()

  lines.forEach(line => {
    const existing = linesByLensId.get(line.lensId)
    if (existing) {
      existing.push(line)
      return
    }
    linesByLensId.set(line.lensId, [line])
  })

  return [...linesByLensId.entries()]
    .map(([lensId, groupLines]) => {
      const sortedLines = [...groupLines].sort(sortCartLinesByPower)
      const firstLine = sortedLines[0]

      return {
        lensId,
        lensCode: firstLine.lensCode,
        lensName: firstLine.lensName,
        lines: sortedLines,
        lineCount: sortedLines.length,
        totalQuantity: sortedLines.reduce((total, line) => total + line.quantity, 0),
      }
    })
    .sort((a, b) => {
      const nameCompare = a.lensName.localeCompare(b.lensName)
      if (nameCompare !== 0) return nameCompare
      const codeCompare = a.lensCode.localeCompare(b.lensCode)
      if (codeCompare !== 0) return codeCompare
      return a.lensId.localeCompare(b.lensId)
    })
}
